import { Synth, Velocity } from "../audio/synth";
import { Note } from "../music/note";
import { NoteCluster } from "../music/part";
import { Instrument } from "./instrument";

/**
 * `at`, sur les gestes : l'instant du geste sur l'horloge de position, en
 * secondes, quand la source le connaît — l'horodatage MIDI. C'est ce que la
 * session live horodate sur l'horloge du service, pour que le trajet mesuré
 * parte du geste et non de l'envoi. Voir `PianoEvent.at`.
 */
export type LaneEvent =
  | { type: 'mute', tick?: number }
  | { type: 'unmute', tick?: number }
  | { type: 'hide' }
  | { type: 'show' }
  | { type: 'noteOn'; note: Note; tick: number; velocity: Velocity; at?: number }
  | { type: 'noteOff'; note: Note; tick: number; at?: number }
  | { type: 'sustainOn'; tick: number; at?: number }
  | { type: 'sustainOff'; tick: number; at?: number }
  | { type: 'clusterChange' }
  /** Tout ce qui était écrit a été effacé, voir `clearNotes`. */
  | { type: 'clear' }

export type PlayedNote = {
  note: Note;
  tick: number;
  tickDuration?: number;
  velocity: Velocity;
  /** Fingering: 1 = thumb … 5 = pinky. */
  finger?: number;
  /**
   * Rang d'écriture de la frappe dans sa lane (`noteOn`), voir
   * `Lane.writeCount`. Absent sur une note chargée d'un morceau.
   */
  writeOrder?: number;
};

/**
 * Une pédale enfoncée : du tick où elle descend à celui où elle se relève.
 */
export type SustainSpan = {
  from: number;
  to: number;
};

/**
 * Premier indice dont le tick dépasse `tick`, dans un tableau trié par tick.
 * Le cas courant — une frappe qui s'ajoute à la fin — se règle sans chercher.
 */
function insertionIndex(items: readonly { tick: number }[], tick: number): number {
  if (items.length === 0 || items[items.length - 1].tick <= tick) {
    return items.length;
  }

  let lo = 0;
  let hi = items.length;

  while (lo < hi) {
    const mid = (lo + hi) >> 1;

    if (items[mid].tick <= tick) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }

  return lo;
}

/**
 * Rendu quand la fenêtre demandée est vide — le cas de presque tous les
 * ticks d'ordonnancement, à 40 Hz et par lane. Gelé : nul ne doit y écrire.
 */
const NO_EVENTS: readonly LaneEvent[] = Object.freeze([]);

/**
 * Rendu par `getSustainSpans` sur une lane sans pédale — le cas de deux
 * morceaux sur trois, à chaque image et pour chaque lane.
 */
const NO_SPANS: readonly SustainSpan[] = Object.freeze([]);

export class Lane {
  private events: any[] = [];
  private notes: PlayedNote[] = [];
  private clusters: NoteCluster[] = [];
  /**
   * Les changements d'état de la pédale, en ordre de tick — ce que lit
   * `isSustainDownAt`.
   *
   * À part des autres événements parce qu'on y cherche un état et non une
   * fenêtre : sans ce tableau, savoir si la pédale est enfoncée à un tick
   * demanderait de remonter `events` jusqu'au dernier `sustainOn`, soit tout
   * le morceau sur une lane qui n'a pas de pédale du tout — et c'est le cas
   * courant, à chaque coup de molette.
   */
  private sustains: { tick: number; down: boolean }[] = [];
  private playingNotes: Set<PlayedNote> = new Set();
  private pressedNotes: Map<number, PlayedNote> = new Map();

  private muted = false;
  private hidden = false;
  /** Frappes écrites depuis la création de la lane ; `clearNotes` n'y touche pas. */
  private writes = 0;
  private enabled: boolean = true;
  private eventListeners = new Set<(event: LaneEvent) => void>();

  /**
   * Track the longest note duration seen in this lane.
   * Used as lookback for binary-search in getNotesInRange().
   */
  private maxNoteDuration = 0;

  constructor(
    private channel: number,
    public synth: Synth,
    private instrument?: Instrument,
    /**
     * Les ticks de cette lane sont datés sur le **transport partagé** et non
     * sur la vue locale.
     *
     * Vrai pour un partenaire de séance : sa note porte sa position dans le
     * morceau, pas la nôtre. Tant que notre vue suit le partagé les deux
     * coïncident et rien ne distingue les deux cas ; dès qu'on met en pause ou
     * qu'on remonte à la molette, ils divergent — et sa note ne doit plus
     * sonner à la seconde où elle arrive, mais quand notre tête de lecture
     * arrivera dessus.
     */
    public readonly hasSharedTimebase = false,
  ) {}


  on(callback: (event: LaneEvent) => void): () => void {
    this.eventListeners.add(callback);

    return () => { this.eventListeners.delete(callback); };
  }

  private emitEvent(event: LaneEvent) {
    for (const l of this.eventListeners) l(event);
  }

  mute(): void {
    this.muted = true;

    this.playingNotes.forEach((n) => {
      this.synth.noteOff(n.note.toMidi(), 0);
    });

    this.playingNotes.clear();
    this.emitEvent({ type: 'mute' });
  }

  unmute(): void {
    this.muted = false;
    this.emitEvent({ type: 'unmute' });
  }

  isMuted(): boolean {
    return this.muted;
  }

  enable(): void {
    this.enabled = true;
  }

  disable(): void {
    this.enabled = false;
  }

  hide(): void {
    this.hidden = true;
    this.emitEvent({ type: 'hide' });
  }

  show(): void {
    this.hidden = false;
    this.emitEvent({ type: 'show' });
  }

  isHidden(): boolean {
    return this.hidden;
  }

  getChannel(): number {
    return this.channel;
  }

  getInstrument(): Instrument | undefined {
    return this.instrument;
  }

  getNotesCount(): number {
    return this.notes.length + this.pressedNotes.size;
  }

  getFirstTick(): number | undefined {
    return this.notes.length > 0 ? this.notes[0].tick : undefined;
  }

  private insertEvent(tick: number, event: LaneEvent) {
    if (!this.enabled) return;

    const insertIndex = insertionIndex(this.events, tick);

    if (insertIndex === this.events.length) {
      this.events.push(event);
    } else {
      this.events.splice(insertIndex, 0, event);
    }

    if (event.type === 'sustainOn' || event.type === 'sustainOff') {
      this.insertSustain(tick, event.type === 'sustainOn');
    }
  }

  private insertSustain(tick: number, down: boolean) {
    const insertIndex = insertionIndex(this.sustains, tick);

    if (insertIndex === this.sustains.length) {
      this.sustains.push({ tick, down });

      return;
    }

    this.sustains.splice(insertIndex, 0, { tick, down });
  }

  private insertNote(playedNote: PlayedNote) {
    const insertIndex = insertionIndex(this.notes, playedNote.tick);

    if (insertIndex === this.notes.length) {
      this.notes.push(playedNote);
    } else {
      this.notes.splice(insertIndex, 0, playedNote);
    }
  }

  addNote(note: Note, tick: number, tickDuration: number, velocity: Velocity, finger?: number) {
    this.insertEvent(tick, { type: 'noteOn', note, tick, velocity });
    this.insertEvent(tick + tickDuration, { type: 'noteOff', note, tick: tick + tickDuration });

    if (tickDuration > this.maxNoteDuration) {
      this.maxNoteDuration = tickDuration;
    }

    if (!this.enabled) return;

    // Insert in sorted order by time
    this.insertNote({ note, tick, tickDuration, velocity, finger });
  }

  /**
   * Forget the notes drawn so far, keeping the lane alive.
   *
   * Only the live piano lane needs this: it has no score behind it, it just
   * accumulates whatever has been played since the page opened. Everywhere
   * else `notes` is the piece and must not be dropped.
   */
  /**
   * Combien de frappes la lane a reçues depuis sa création. Une note dont le
   * `writeOrder` est au moins ce nombre a été jouée après qu'on l'a lu.
   */
  get writeCount(): number {
    return this.writes;
  }

  clearNotes(): void {
    // Les trois tableaux, pas seulement `notes` : les événements et les
    // groupes suivaient la séance entière, et la lane vive grossissait
    // jusqu'à la fermeture de l'onglet.
    this.notes = [];
    this.events = [];
    this.clusters = [];
    this.sustains = [];
    this.pressedNotes.clear();
    this.maxNoteDuration = 0;

    // Les ordonnanceurs tiennent un indice dans `events` : ils le remettent à
    // zéro sur ce signal.
    this.emitEvent({ type: 'clear' });
  }

  noteOn(note: Note, tick: number, velocity: Velocity, at?: number) {
    this.insertEvent(tick, { type: 'noteOn', note, tick, velocity });

    const playedNote: PlayedNote = { note, tick, velocity, writeOrder: this.writes++ };
    this.pressedNotes.set(note.toMidi(), playedNote);

    this.emitEvent({ type: 'noteOn', note, tick, velocity, at });

    if (!this.enabled) return;

    // Insert into this.notes in sorted order so getNotesInRange() can find it
    this.insertNote(playedNote);
  }

  noteOff(note: Note, tick: number, at?: number) {
    this.insertEvent(tick, { type: 'noteOff', note, tick });

    // Finalize the pressed note with its computed duration.
    // Since pressedNotes stores the same object reference as this.notes,
    // mutating tickDuration here updates both places at once.
    const midi = note.toMidi();
    const pressed = this.pressedNotes.get(midi);
    if (pressed) {
      pressed.tickDuration = tick - pressed.tick;
      if (pressed.tickDuration > this.maxNoteDuration) {
        this.maxNoteDuration = pressed.tickDuration;
      }
      this.pressedNotes.delete(midi);
    }

    this.emitEvent({ type: 'noteOff', note, tick, at });
  }

  /**
   * Écrit un mouvement de pédale sans l'annoncer : le pendant d'`addNote` pour
   * la pédale.
   *
   * On charge un morceau, personne n'appuie sur rien — et un `sustainOn` émis
   * ferait lever l'étouffoir à l'instant du chargement, pour tout ce qui sonne
   * alors.
   */
  addSustain(tick: number, down: boolean) {
    this.insertEvent(tick, down ? { type: 'sustainOn', tick } : { type: 'sustainOff', tick });
  }

  /**
   * Les pédales enfoncées qui touchent la fenêtre [`fromTick`, `toTick`].
   *
   * `openEnd` borne celle qui n'a pas de levée connue, et les deux cas ne
   * veulent pas dire la même chose : sur un jeu en cours c'est la tête de
   * lecture, parce que le pied est encore en bas et que l'avenir n'est pas
   * écrit ; sur un morceau c'est la fin de la fenêtre, parce que sa pédale
   * est notée jusqu'au bout et qu'on a le droit de la montrer devant la tête.
   */
  getSustainSpans(fromTick: number, toTick: number, openEnd: number): readonly SustainSpan[] {
    if (this.sustains.length === 0) {
      return NO_SPANS;
    }

    const spans: SustainSpan[] = [];
    let index = insertionIndex(this.sustains, fromTick);
    let from = index > 0 && this.sustains[index - 1].down ? this.sustains[index - 1].tick : null;

    for (; index < this.sustains.length; index++) {
      const change = this.sustains[index];

      if (change.tick > toTick) {
        break;
      }

      if (change.down) {
        from ??= change.tick;

        continue;
      }

      if (from !== null) {
        spans.push({ from, to: change.tick });
        from = null;
      }
    }

    if (from !== null) {
      const to = Math.min(openEnd, toTick);

      if (to > from) {
        spans.push({ from, to });
      }
    }

    return spans;
  }

  /**
   * La pédale est-elle enfoncée à ce tick ?
   *
   * L'état, et non l'événement : quand la tête de lecture saute au milieu
   * d'une pédale tenue, son enfoncement ne sera jamais relu, et il faut
   * pourtant retenir les notes qui suivent. Voir le `seek` de `Scheduler`.
   */
  isSustainDownAt(tick: number): boolean {
    const next = insertionIndex(this.sustains, tick);

    return next > 0 && this.sustains[next - 1].down;
  }

  sustainOn(tick: number, at?: number) {
    this.insertEvent(tick, { type: 'sustainOn', tick });
    this.emitEvent({ type: 'sustainOn', tick, at });
  }

  sustainOff(tick: number, at?: number) {
    this.insertEvent(tick, { type: 'sustainOff', tick });
    this.emitEvent({ type: 'sustainOff', tick, at });
  }

  /**
   * Get notes whose sounding range overlaps [fromTick, toTick].
   * A note is included if it starts before toTick AND ends after fromTick,
   * so long-held notes that began before the window are not missed.
   *
   * Uses binary search on the sorted `this.notes` array, O(log n + k)
   * instead of scanning all events and doing O(n) noteOff lookups.
   */
  getNotesInRange(fromTick: number, toTick: number): PlayedNote[] {
    const notes = this.notes;
    const result: PlayedNote[] = [];

    // Lookback: a note starting before fromTick can still be sounding if
    // its duration extends into the window.  Use the longest known duration
    // (with a minimum floor of 960 ticks / 1 beat) so we never miss notes.
    const lookback = Math.max(this.maxNoteDuration, 960);
    let searchTick = fromTick - lookback;

    // Une note encore tenue n'a pas de durée : `maxNoteDuration` ne la compte
    // pas, et passé ce recul elle sortait de la fenêtre tant que la touche
    // restait enfoncée. On recule jusqu'à la plus ancienne.
    for (const pressed of this.pressedNotes.values()) {
      if (pressed.tick < searchTick) {
        searchTick = pressed.tick;
      }
    }

    // Binary search: find first index where note.tick >= searchTick
    let lo = 0;
    let hi = notes.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (notes[mid].tick < searchTick) lo = mid + 1;
      else hi = mid;
    }

    // Iterate from lo, stop when note.tick >= toTick (no note starting
    // at or after toTick can overlap the window).
    for (let i = lo; i < notes.length; i++) {
      const n = notes[i];
      if (n.tick >= toTick) break;

      // noteEnd = tick + duration, or Infinity if the note is still held
      const noteEnd = n.tickDuration !== undefined
        ? n.tick + n.tickDuration
        : Infinity;

      if (noteEnd > fromTick) {
        result.push(n);
      }
    }

    return result;
  }

  /**
   * Les événements depuis `startIndex` jusqu'à `toTick` inclus.
   *
   * Le tableau rendu est en lecture seule : fenêtre vide, c'est une constante
   * partagée, et l'allouer à chaque tick d'ordonnancement était du travail
   * pour rien.
   */
  getEventsFromIndex(startIndex: number, toTick: number): readonly LaneEvent[] {
    if (startIndex >= this.events.length || this.events[startIndex].tick > toTick) {
      return NO_EVENTS;
    }

    const result: LaneEvent[] = [];

    for (let i = startIndex; i < this.events.length; i++) {
      const event = this.events[i];
      if (event.tick > toTick) break;
      result.push(event);
    }

    return result;
  }

  /**
   * Accumulate this lane's per-pitch note counts into `counts` (indexed by MIDI
   * number, length 128). Used to build a pitch histogram across all lanes for a
   * robust (outlier-trimmed) tessitura. In-place to avoid per-lane allocation.
   */
  accumulatePitchCounts(counts: number[]): void {
    for (const n of this.notes) {
      const midi = n.note.toMidi();

      if (midi >= 0 && midi < counts.length) {
        counts[midi]++;
      }
    }
  }

  /**
   * Return the MIDI note range (min, max) across all notes in this lane.
   * Returns undefined if the lane has no notes.
   */
  getNoteRange(): { min: number; max: number } | undefined {
    if (this.notes.length === 0) return undefined;

    let min = 127;
    let max = 0;

    for (const n of this.notes) {
      const midi = n.note.toMidi();
      if (midi < min) min = midi;
      if (midi > max) max = midi;
    }

    return { min, max };
  }

  addCluster(cluster: NoteCluster): void {
    const i = this.clusters.findIndex(c => c.startTick > cluster.startTick);
    if (i === -1) this.clusters.push(cluster);
    else this.clusters.splice(i, 0, cluster);
    this.emitEvent({ type: 'clusterChange' });
  }

  /** Notify listeners that an existing cluster was mutated in place. */
  notifyClusterChange(): void {
    this.emitEvent({ type: 'clusterChange' });
  }

  getClustersInRange(fromTick: number, toTick: number): NoteCluster[] {
    const result: NoteCluster[] = [];
    for (const c of this.clusters) {
      if (c.startTick >= toTick) break;
      if (c.endTick > fromTick) result.push(c);
    }
    return result;
  }

  /**
   * Return the index of the first event whose tick >= the given tick.
   * Returns events.length when every event is before the tick.
   */
  getIndexFromTick(tick: number): number {
    const events = this.events;
    let lo = 0;
    let hi = events.length;

    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (events[mid].tick < tick) lo = mid + 1;
      else hi = mid;
    }

    return lo;
  }

  destroy(): void {
    this.synth.dispose();
    this.notes.length = 0;
    this.clusters.length = 0;
    this.events.length = 0;
    this.sustains.length = 0;
    this.pressedNotes.clear();
    this.playingNotes.clear();
    this.eventListeners.clear();
  }
}

